import { get } from 'lodash';
import * as R from 'remeda';
import { IntlShape } from 'react-intl';
import { getIDFromUrl } from '@rossum/api-client';
import { isNotNullOrUndefined } from '../../../lib/typeGuards';
import { Url } from '../../../types/basic';
import { Group, GroupRole, groupRoles, Groups } from '../../../types/group';
import { GroupType } from './types';

const groupValues: GroupType = {
  organization_group_admin: { view: 5, importance: 0 },
  admin: { view: 3, importance: 1 },
  empowered_manager: { view: 2.5, importance: 1.5 },
  manager: { view: 2, importance: 2 },
  annotator: { view: 0, importance: 3 },
  annotator_limited: { view: 1, importance: 4 },
  annotator_embedded: { view: 1, importance: 4 },
  viewer: { view: 4, importance: 5 },
  approver: { view: 6, importance: 6 },
};

export const roleNames = R.keys(groupValues);

const compareGroups = (group: Group) => {
  const role = groupValues[group.name] || groupValues.viewer;
  return role.importance;
};

const compareGroupsForSelect = (group: Group) => {
  const role = groupValues[group.name] || groupValues.viewer;
  return role.view;
};

export const isApproverRole = (urls: Array<Url>, groups: Groups) => {
  const approverGroup = groups.find(({ name }) => name === 'approver');
  return approverGroup !== undefined && urls.includes(approverGroup.url);
};

export const isViewerRole = (urls: Array<Url>, groups: Groups) => {
  const viewerGroup = groups.find(({ name }) => name === 'viewer');
  return viewerGroup !== undefined && urls.includes(viewerGroup.url);
};

export const getRole = (urls: Array<Url>, groups: Groups) =>
  R.pipe(
    groups,
    R.filter(({ url }) => urls.includes(url)),
    R.sortBy(compareGroups),
    R.first()
    // This is what was there before, it's very incorrect though, and it broke a whole bunch of stuff that's unsafe
  ) as Group;

export const getRoleId = (name: string, groups: Groups) =>
  groups.find(g => g.name === name)?.id;

export const getRoleUrlByName = (name: string, groups: Groups) =>
  groups.find(g => g.name === name)?.url;

export const getRoleName = (urls: Array<Url>, groups: Groups) =>
  get(getRole(urls, groups), 'name');

export const getRoleUrl = (urls: Array<Url>, groups: Groups) =>
  get(getRole(urls, groups), 'url');

export const isUserAdminLike = (role: GroupRole) =>
  ['admin', 'organization_group_admin'].includes(role);

export const getRoleOptions = (groups: Groups, intl: IntlShape) =>
  R.pipe(
    groupRoles,
    // This makes sure we don't add any role that f/e is not familiar with.
    R.map(role => groups.find(group => group.name === role)),
    R.filter(isNotNullOrUndefined),

    R.sortBy(compareGroupsForSelect),
    R.map(({ url, name }) => ({
      name,
      label: intl.formatMessage({
        id: `containers.settings.users.roles.${name}`,
      }),
      value: url,
    }))
  );

export const canManageQueue = ({
  isAdminLike,
  isEmpoweredManager,
  userQueueUrls,
  queueUrl,
}: {
  isAdminLike: boolean;
  isEmpoweredManager: boolean;
  userQueueUrls: Array<Url>;
  queueUrl: Url;
}): boolean => {
  if (isAdminLike) {
    return true;
  }
  if (isEmpoweredManager) {
    return userQueueUrls.includes(queueUrl);
  }
  return false;
};

export const userHasQueueId = (
  queueId: number,
  queueUrls: Array<Url>
): boolean => queueUrls.some(url => getIDFromUrl(url) === queueId);
